import { Fragment, useMemo, useState, useReducer, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Button,
  ComposedModal,
  ModalBody,
  ModalHeader,
} from "@carbon/react";
import { WarningAlt } from "@carbon/icons-react";
import { Loader, Chat, ClosedBiddingNotice } from "../../components";
import ConfirmDialog from "../../components/carbon/ConfirmDialog/ConfirmDialog";
import { useID } from "../../hooks";
import { useBiddingClosed } from "../../hooks/useBiddingClosed";
import BidResultTable from "./BidResultTable";
import BuyerConditionsPanel from "../Offer/BuyerConditionsPanel";
import SellerOfferHeader from "./SellerOfferHeader";
import SellerOfferConditions from "./SellerOfferConditions";
import JointSellerOfferLines from "./JointSellerOfferLines";
import JointSellerOfferActions from "./JointSellerOfferActions";
import JointPendingOffers from "./JointPendingOffers";
import { useJointSellerEditing } from "./useJointSellerEditing";
import { ORDER_TYPES, OfferResponse, OrderState } from "../../types";
import {
  useCreateChat,
  useCreateMessage,
  useMultipleOrderChat,
} from "../../hooks/useChat";
import {
  useMultipleOffer,
  useUpdateMultipleInverseOffer,
  useJointOrderOfferStatus,
  useUpdateOfferDetails,
  useDeclineMultipleOffers,
  useUndeclineMultipleOffers,
} from "../../hooks/useOffers";
import { queryKeys } from "../../hooks/queryKeys";
import {
  allLinesPriced,
  calculateMinimumDecrement,
  linePricesPayload,
  pricedLinesOf,
} from "../../utils";
import { useNotification } from "../../context";
import InverseBidNotice from "./InverseBidNotice";
import "./bid.scss";
import ServicesToQuoteCard from "../../components/ServicesToQuoteCard/ServicesToQuoteCard";
import { servicesToQuoteAboveTheQuote } from "../Order/serviceDetail";

const actions = {
  UPDATE_OFFER_PRICE: "UPDATE_OFFER_PRICE",
};

const JointSellerOffer = () => {
  const { t } = useTranslation();
  const id = useID();
  const navigate = useNavigate();
  const { openNotification, openError } = useNotification();
  const [openChat, setOpenChat] = useState(false);
  const [searchParams] = useSearchParams();
  const {
    editingOffers,
    updatingOfferId,
    setUpdatingOfferId,
    onStartEdit,
    onCancelEdit,
    onEditPriceChange,
    onEditCurrencyChange,
    onEditLinePriceChange,
    resetEditing,
  } = useJointSellerEditing();
  const [offerOrderMap, setOfferOrderMap] = useState<Record<string, number>>(
    {}
  );
  const [openDeclineConfirm, setOpenDeclineConfirm] = useState(false);

  const [event, updateEvent] = useReducer(
    (state: any, action: any) => {
      const newState = { ...state };
      switch (action.type) {
        case actions.UPDATE_OFFER_PRICE: {
          const price = parseFloat(action.value);
          const offerIndex = newState.offersToUpdate.findIndex(
            (offer: OfferResponse) => offer.id === action.offerId
          );
          if (isNaN(price) || price === 0) {
            if (offerIndex !== -1)
              newState.offersToUpdate.splice(offerIndex, 1);
            delete newState.errors[action.offerId];
            return newState;
          }

          if (price > parseFloat(action.minDecrement) || price < 0) {
            newState.errors[action.offerId] =
              `${t("validations.lowerThanX")} ${action.minDecrement}.`;
          } else {
            delete newState.errors[action.offerId];
            if (offerIndex !== -1) {
              newState.offersToUpdate[offerIndex].price = price;
            } else {
              newState.offersToUpdate.push({ id: action.offerId, price });
            }
          }
          return newState;
        }
      }
    },
    { offersToUpdate: [], errors: {} }
  );

  const multipleOrderId = searchParams.get("multipleOrderId");

  const { data: multipleOrderChat, isLoading: isLoadingChat } =
    useMultipleOrderChat(multipleOrderId || "");

  const { mutate: mutateChat, isPending: isLoadingCreateChat } = useCreateChat([
    queryKeys.MULTIPLE_ORDER_CHAT,
  ]);

  const { mutate: mutateMessage, isPending: isLoadingMessage } =
    useCreateMessage([queryKeys.MULTIPLE_ORDER_CHAT]);

  const { mutate: updateMultipleInverseOffer, isPending: isUpdating } =
    useUpdateMultipleInverseOffer(
      () => openNotification(t("tenders.seller.modifiedOffer")),
      (error) => openError(error)
    );

  const { mutate: updateOfferDetails } = useUpdateOfferDetails(
    () => {
      openNotification(t("tenders.seller.modifiedOffer"));
      resetEditing();
    },
    (error) => {
      openError(error);
      setUpdatingOfferId(null);
    }
  );

  const { mutate: declineMultipleOffers, isPending: isDeclining } =
    useDeclineMultipleOffers(
      () => {
        setOpenDeclineConfirm(false);
        openNotification(t("tenders.seller.modifiedOffer"));
      },
      (error) => openError(error)
    );

  const { mutate: undeclineMultipleOffers, isPending: isUndeclining } =
    useUndeclineMultipleOffers(
      () => openNotification(t("tenders.seller.modifiedOffer")),
      (error) => openError(error)
    );

  const {
    data: multipleOffer,
    isLoading: isLoadingOffers,
    isError: offerError,
  } = useMultipleOffer(id);

  const { data: jointOfferStatus, isLoading: isLoadingStatus } =
    useJointOrderOfferStatus(multipleOrderId || "", !!multipleOrderId);

  // Capture the initial order of offers to maintain stable positions.
  useEffect(() => {
    if (
      multipleOffer &&
      multipleOffer[0] &&
      Object.keys(offerOrderMap).length === 0
    ) {
      const orderMap: Record<string, number> = {};
      multipleOffer[0].forEach((offer: OfferResponse, index: number) => {
        orderMap[offer.id] = index;
      });
      setOfferOrderMap(orderMap);
    }
  }, [multipleOffer, offerOrderMap]);

  const stableOffers = useMemo(() => {
    if (!multipleOffer || !multipleOffer[0]) return [];
    if (Object.keys(offerOrderMap).length > 0) {
      return [...multipleOffer[0]].sort(
        (a, b) => (offerOrderMap[a.id] ?? 999) - (offerOrderMap[b.id] ?? 999)
      );
    }
    return multipleOffer[0];
  }, [multipleOffer, offerOrderMap]);

  const selectedOffers = useMemo(
    () => stableOffers.filter((offer: OfferResponse) => offer.selected) || [],
    [stableOffers]
  );

  const offerIds = useMemo(
    () => stableOffers.map((offer: OfferResponse) => offer.id),
    [stableOffers]
  );

  const isDeclined = useMemo(
    () =>
      stableOffers.length > 0 &&
      stableOffers.every((offer: OfferResponse) => offer.declined === true),
    [stableOffers]
  );

  const jointOrderState = stableOffers[0]?.order?.state;
  // The lines of a joint order close together; the first one speaks for all.
  const biddingClosed = useBiddingClosed(stableOffers[0]?.order);
  const canShowDeclineToggle =
    !!jointOrderState &&
    jointOrderState !== OrderState.CANCELLED &&
    jointOrderState !== OrderState.SUCCEED &&
    jointOrderState !== OrderState.CLOSED &&
    !biddingClosed;

  const onSubmitUpdate = () => {
    if (event?.offersToUpdate.length > 0 && isOrderOpen) {
      updateMultipleInverseOffer(
        event.offersToUpdate.map((offer: any) => ({
          offer: { id: offer.id },
          price: offer.price,
        }))
      );
    }
  };

  const onSaveEdit = (offerId: string) => {
    const editData = editingOffers[offerId];
    if (!editData || !editData.price || !editData.currency) return;
    const offerToUpdate = stableOffers.find(
      (offer: OfferResponse) => offer.id === offerId
    );
    if (!offerToUpdate) return;
    if (
      offerToUpdate.order?.state === OrderState.OPEN &&
      !biddingClosed &&
      !offerToUpdate.selected
    ) {
      // SCRUM-116: a line-priced offer sends its lines; the backend recomputes the total.
      const pricedLines = pricedLinesOf(offerToUpdate.order);
      if (
        pricedLines.length > 0 &&
        !allLinesPriced(pricedLines, editData.linePrices ?? {})
      ) {
        return;
      }
      setUpdatingOfferId(offerId);
      updateOfferDetails(
        pricedLines.length > 0
          ? {
              offerId,
              currency: editData.currency,
              linePrices: linePricesPayload(
                pricedLines,
                editData.linePrices ?? {}
              ),
            }
          : {
              offerId,
              price: parseFloat(editData.price),
              currency: editData.currency,
            }
      );
    }
  };

  const onInverseChange = (offer: OfferResponse, value: string) =>
    updateEvent({
      type: actions.UPDATE_OFFER_PRICE,
      value,
      offerId: offer?.id,
      minDecrement: calculateMinimumDecrement(
        offer?.price,
        offer?.order?.minimumDecrement
      ),
      orderMinimumDecrement: offer?.order?.minimumDecrement,
    });

  if (offerError) navigate("/not-found");

  if (
    isLoadingOffers ||
    isUpdating ||
    isLoadingStatus ||
    isDeclining ||
    isUndeclining
  )
    return <Loader isFullScreen />;

  const hasProductsWithoutOffers =
    jointOfferStatus?.ordersWithoutOffers &&
    jointOfferStatus.ordersWithoutOffers.length > 0;

  const isOrderOpen =
    multipleOffer &&
    multipleOffer[0] &&
    multipleOffer[0][0]?.order?.state === OrderState.OPEN &&
    !biddingClosed;

  if (!(multipleOffer && multipleOffer.length && stableOffers.length))
    return null;

  const representative = multipleOffer[0][0];

  return (
    <Fragment>
      <div className="seller-offer__page">
        <SellerOfferHeader
          order={representative.order}
          orderName={representative?.order?.name}
          selected={selectedOffers.length > 0}
          chatCount={multipleOrderChat?.messages?.length || 0}
          onOpenChat={() => setOpenChat(true)}
          canDecline={canShowDeclineToggle && !isDeclined}
          isDeclining={isDeclining}
          onDecline={() => setOpenDeclineConfirm(true)}
        />

        {selectedOffers && selectedOffers.length > 0 && (
          <BidResultTable
            selectedOffers={selectedOffers}
            title={t("tenders.seller.accepted")}
          />
        )}

        <ServicesToQuoteCard services={servicesToQuoteAboveTheQuote(stableOffers.map((o: any) => o.order))} />

        <div className="seller-offer__panels">
          <BuyerConditionsPanel
            order={representative.order}
            itemCount={stableOffers.length}
          />
          <SellerOfferConditions offer={representative} />
        </div>

        {isDeclined && (
          <div className="seller-offer__decline-banner">
            <p>{t("offer.declineOffer.declinedBanner")}</p>
            {canShowDeclineToggle && (
              <Button
                kind="primary"
                size="sm"
                disabled={isUndeclining}
                onClick={() => undeclineMultipleOffers(offerIds)}
              >
                {isUndeclining
                  ? t("cta.loading")
                  : t("offer.declineOffer.undo")}
              </Button>
            )}
          </div>
        )}

        {isOrderOpen &&
          !isDeclined &&
          stableOffers.some(
            (o: OfferResponse) => o?.order?.type === ORDER_TYPES.INVERSE_JOINT
          ) && (
            <InverseBidNotice
              decrement={representative.order?.minimumDecrement}
            />
          )}

        <JointSellerOfferLines
          offers={stableOffers}
          isDeclined={isDeclined}
          editingOffers={editingOffers}
          updatingOfferId={updatingOfferId}
          errors={event.errors}
          onInverseChange={onInverseChange}
          onStartEdit={onStartEdit}
          onEditPriceChange={onEditPriceChange}
          onEditCurrencyChange={onEditCurrencyChange}
          onEditLinePriceChange={onEditLinePriceChange}
        />

        {biddingClosed && (
          <ClosedBiddingNotice
            closesAt={representative.order?.closesAt}
            finishDate={representative.order?.finishDate}
          />
        )}

        {jointOrderState === OrderState.CONSULTATION && (
          <div className="seller-offer__notice">
            <WarningAlt size={20} />
            <p>{t("offer.consultationBlocked")}</p>
          </div>
        )}

        {hasProductsWithoutOffers && isOrderOpen && (
          <JointPendingOffers
            jointOfferStatus={jointOfferStatus}
            multipleOrderId={multipleOrderId}
          />
        )}

        <JointSellerOfferActions
          offers={stableOffers}
          isOrderOpen={!!isOrderOpen}
          isDeclined={isDeclined}
          canShowDeclineToggle={canShowDeclineToggle}
          editingOffers={editingOffers}
          updatingOfferId={updatingOfferId}
          errorsCount={Object.keys(event.errors).length}
          offersToUpdateCount={event.offersToUpdate.length}
          isUpdating={isUpdating}
          isDeclining={isDeclining}
          onOpenDecline={() => setOpenDeclineConfirm(true)}
          onInverseUpdate={onSubmitUpdate}
          onCancelEdit={onCancelEdit}
          onSaveEdit={onSaveEdit}
        />

        <div className="seller-offer__disclaimer">
          <p>{t("tenders.disclaimer")}</p>
        </div>
      </div>

      <ComposedModal
        open={openChat}
        onClose={() => setOpenChat(false)}
        size="md"
      >
        <ModalHeader title={t("chat.title")} />
        <ModalBody hasForm>
          <Chat
            isOrder
            isMultiple
            chat={multipleOrderChat}
            onCreateChat={mutateChat}
            isLoadingChat={
              isLoadingChat || isLoadingCreateChat || isLoadingMessage
            }
            onCreateMessage={mutateMessage}
            order={representative?.order}
          />
        </ModalBody>
      </ComposedModal>

      <ConfirmDialog
        open={openDeclineConfirm}
        danger
        title={t("offer.declineOffer.confirm.title")}
        body={<p>{t("offer.declineOffer.confirm.body")}</p>}
        confirmLabel={t("offer.declineOffer.confirm.cta")}
        isPending={isDeclining}
        onConfirm={() => declineMultipleOffers(offerIds)}
        onCancel={() => setOpenDeclineConfirm(false)}
      />
    </Fragment>
  );
};

export default JointSellerOffer;
